import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import styled from "styled-components";
import { SlidersIcon } from "../../icons/glyphs";
import { isRenderableNode } from "../../utils/reactNode";
import { AnchoredPopup, type AnchoredPopupAnchor } from "../AnchoredPopup";
import { TooltipIconButton } from "../IconButton";
import { Scrollbar } from "../Scrollbar";
import type { MediaTimelineTrackBase } from "./timelineTypes";
import { useBoundaryFocusRescue } from "./useBoundaryFocusRescue";

const AttachedTools = styled(AnchoredPopup)`
  background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  border: 1px solid ${({ theme }) => theme.color.border.strong};
  box-shadow: none;

  &[data-placement^="top"] {
    border-bottom: none;
    border-radius: ${({ theme }) => theme.shape.radius.surface}
      ${({ theme }) => theme.shape.radius.surface} 0 0;
  }
  &[data-placement^="bottom"] {
    border-top: none;
    border-radius: 0 0 ${({ theme }) => theme.shape.radius.surface}
      ${({ theme }) => theme.shape.radius.surface};
  }
`;

const ToolsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  min-width: 100%;
  min-height: 2.75rem;
  box-sizing: border-box;
  padding: 0.375rem 0.5rem;
`;

/** One disclosed track per timeline. Data/actions remain owned by the toolbar children. */
export function useTrackToolbar(
  tracks: readonly MediaTimelineTrackBase[],
  available: boolean,
  fallbackFocus: RefObject<HTMLElement | null>,
  onExitToTimeline: () => void,
  onPointerClose: (source: HTMLElement) => void,
  onPointerOpen: (source: HTMLElement) => void,
) {
  const { arm } = useBoundaryFocusRescue();
  const frames = useRef(new Map<string, HTMLDivElement>());
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const [openId, setOpenId] = useState<string | null>(null);
  // Pointer opens keep playback focus (Space plays); keyboard opens step into the first control.
  const [openedByPointer, setOpenedByPointer] = useState(false);
  const active = available
    ? tracks.find((track) => track.id === openId && isRenderableNode(track.toolbar))
    : undefined;
  const anchor = useMemo<AnchoredPopupAnchor | null>(() => {
    const frame = openId === null ? undefined : frames.current.get(openId);
    return frame ? { type: "rect", el: frame, rect: frame.getBoundingClientRect() } : null;
  }, [openId]);
  const close = useCallback(() => {
    setOpenId(null);
    const trigger = openId === null ? undefined : triggers.current.get(openId);
    if (trigger) onPointerClose(trigger);
  }, [onPointerClose, openId]);
  const exitToTimeline = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") return;
    if (event.nativeEvent.isComposing) {
      // IME 취소는 브라우저에 맡기되 document의 popup dismiss까지 전파하지 않는다.
      event.stopPropagation();
      return;
    }
    // 트랙 도구의 Escape는 하위 팝업까지 종료하고 곧바로 Space 재생으로 돌아간다.
    event.preventDefault();
    event.stopPropagation();
    close();
    onExitToTimeline();
  };
  useEffect(() => {
    if (openId !== null && !active) setOpenId(null);
  }, [active, openId]);

  const nameOf = (track: MediaTimelineTrackBase) =>
    track.toolbarLabel?.trim() || `${track.label ?? track.id} tools`;

  return {
    activeId: active?.id,
    registerFrame(id: string, node: HTMLDivElement | null) {
      if (node) frames.current.set(id, node);
      else frames.current.delete(id);
    },
    renderTrigger(track: MediaTimelineTrackBase) {
      if (!isRenderableNode(track.toolbar)) return null;
      const expanded = active?.id === track.id;
      return (
        <TooltipIconButton
          ref={(node) => {
            if (node) triggers.current.set(track.id, node);
            else triggers.current.delete(track.id);
          }}
          size="xs"
          tooltip={nameOf(track)}
          aria-label={nameOf(track)}
          aria-haspopup="dialog"
          data-media-timeline-tools-trigger=""
          aria-expanded={expanded}
          selected={expanded}
          disabled={!available}
          onKeyDownCapture={exitToTimeline}
          onPointerDown={(event) => {
            // 같은 트리거의 pointerdown dismiss → click 재열기를 막는다.
            if (expanded) event.stopPropagation();
          }}
          onClick={(event) => {
            setOpenId(expanded ? null : track.id);
            setOpenedByPointer(event.detail > 0);
            if (event.detail > 0) {
              if (expanded) onPointerClose(event.currentTarget);
              // A pointer open leaves focus on the trigger (no autofocus), so hand it to playback.
              else onPointerOpen(event.currentTarget);
            }
          }}
        >
          <SlidersIcon />
        </TooltipIconButton>
      );
    },
    popup: active ? (
      <AttachedTools
        key={active.id}
        open
        anchor={anchor}
        onClose={close}
        placement="top-start"
        offset={-1}
        width="matchAnchor"
        maxWidth="max-content"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- Keyboard activation moves focus into the tools; pointer keeps playback focus.
        autoFocus={openedByPointer ? false : "firstFocusable"}
        returnFocus
        repositionOnScroll
        repositionOnResize
        aria-label={nameOf(active)}
      >
        <Scrollbar
          orientation="both"
          gutter="auto"
          style={{ width: "100%", maxWidth: "calc(100vw - 1rem)" }}
          onFocusCapture={(event) => {
            // 자식 portal의 focus는 해당 팝업의 위치·포커스 처리에 맡긴다.
            if (event.currentTarget.contains(event.target))
              event.target.scrollIntoView({ block: "nearest", inline: "nearest" });
          }}
        >
          <ToolsRow
            onKeyDownCapture={exitToTimeline}
            onClickCapture={(event) => {
              const focused = event.currentTarget.ownerDocument.activeElement;
              if (event.detail === 0 && focused instanceof HTMLElement)
                arm(focused, () => fallbackFocus.current);
            }}
          >
            {active.toolbar}
          </ToolsRow>
        </Scrollbar>
      </AttachedTools>
    ) : null,
  };
}
